import React from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  ReferenceLine,
  Area,
} from 'recharts';

import { useContent } from '../../Content/cms';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import { Spinner } from 'react-bootstrap';

// Tipos
export interface Score {
  name: string;
}

export interface Population {
  fill_color: string
  stroke_color: string
  x: number[];
  y: number[];
}

export interface Chart {
  name: string;
  display_configuration: object;
}

export interface ChartData {
  protocol_item: number
  lambda_function: number
  population: number
  score: Score
  chart: Chart
  population_distribution?: Population | null
  value?: number | null
}

//Estructura usada para alimentar Recharts (con x e y combinados), comop necesita la libreria
interface DistributionPoint {
  x: number;
  y: number;
}

interface GroupedProtocol {
  protocol: number;
  items: ChartData[];
}

interface RenderReportChartsProps {
  data: ChartData[];
  itemTitles: Record<number, string>;
  ferFuncNames: Record<number, string>;
}

// Función para agrupar  por protocol Item y convertir en array
const groupByProtocolItem = (data: ChartData[]): GroupedProtocol[] => {
  const grouped: Record<number, ChartData[]> = {};

  data.forEach((item) => {
    const protocol = item.protocol_item;
    if (!grouped[protocol]) {
      grouped[protocol] = [];
    }
    grouped[protocol].push(item);
  });

  return Object.keys(grouped).map((key) => ({
    protocol: Number(key),
    items: grouped[Number(key)],
  }));
};

const RenderReportCharts: React.FC<RenderReportChartsProps> = (props) => {

  let { data, itemTitles, ferFuncNames } = props;

  let getContent = useContent();

  const groupedData = groupByProtocolItem(data);

  function getItemDistributionSeries(population_distribution: Population): DistributionPoint[] {
    return population_distribution.x.map((xVal, i) => ({
      x: xVal,
      y: population_distribution.y[i],
    }));
  }

  return (
    <Container>
      {groupedData
        .filter(({ items }) =>
          items.some(
            (item) =>
              item.population_distribution !== null &&
              item.value !== null &&
              item.value !== undefined
          )
        )
        .map(({ protocol, items }) => (
          <div key={protocol}>
            <h6 className={'mb-3 fw-bolder bg-info-light p-3'}>
              {itemTitles[protocol]}
            </h6>

            {items
              .filter(
                (item) =>
                  item.population_distribution !== null &&
                  item.value !== null &&
                  item.value !== undefined
              )
              .map((item, index) => {
                return (
                  <Row
                    key={index}
                    className={'pb-4 pt-3 border-bottom d-flex align-items-center'}
                    style={{ maxWidth: '100%' }}
                  >
                    <p
                      className={'fw-bolder mb-0'}
                      style={{
                        width: '250px',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {
                        getContent(
                          "protocol_result__feature_extraction__" +
                          ferFuncNames[item.lambda_function] +
                          '__' +
                          item.score.name + "__name"
                        )
                      }
                    </p>

                    {/** Data ready */}
                    {(item.population_distribution && item.value !== undefined && item.value !== null) && (
                      <Col
                        className={'col-4 px-3 mx-3'}
                        style={{ flexShrink: 0 }}
                      >
                        <ResponsiveContainer
                          width="100%"
                          height={100}
                          className={'p-3 mt-2'}
                        >
                          <AreaChart
                            data={getItemDistributionSeries(
                              item.population_distribution
                            )}
                          >
                            <XAxis
                              dataKey="x"
                              domain={['dataMin', 'dataMax']}
                              type="number"
                              tickFormatter={(x: number) => x.toFixed(2)}
                              interval="preserveStartEnd"
                              tick={{ fontSize: 12 }}
                            />
                            <YAxis type="number" hide />
                            <Area
                              type="monotone"
                              dataKey="y"
                              stroke={item.population_distribution.stroke_color}
                              fill={item.population_distribution.fill_color}
                            />
                            <ReferenceLine
                              x={item.value}
                              stroke="#ba1c1a"
                              strokeWidth={1}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Col>
                    )}

                    <Col
                      className={'pl-3 ml-3 pt-4'}
                      style={{ flexShrink: 1, flexGrow: 1 }}
                    >
                      <p>
                        <strong>
                          {getContent(
                            "feature_display__score_display__current_value"
                          ) + ": "}
                        </strong>
                        {item.value !== undefined ? (
                          item.value !== null ? item.value.toFixed(2) : "N/A"
                        ) : (
                          <Spinner
                            className={'ml-3'}
                            animation={'border'}
                            size={'sm'}
                          />
                        )}
                      </p>
                      <small
                        style={{
                          width: '300px',
                          wordWrap: 'break-word',
                          whiteSpace: 'normal',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        {getContent(
                          `protocol_result__feature_extraction__${ferFuncNames[item.lambda_function]}__${item.score.name}__short_explanation`
                        )}
                      </small>
                    </Col>
                  </Row>
                );
              })}
          </div>
        ))}
    </Container>
  );
};

export default RenderReportCharts;
